import { IconChevronDownSmall } from "@/assets/icons/geist/IconChevronDownSmall";
import { EditorIcon } from "@/components/environments/EditorIcon";
import { ButtonGroup, ButtonGroupItem } from "@/components/flexkit/ButtonGroup";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuPortal,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { Tooltip } from "@/components/Tooltip";
import { useGetEditorForEnv, useSetEditorIdForEnv } from "@/hooks/use-editor-for-env";
import { useOpenEditor } from "@/hooks/use-open-editor";
import { useOpenEditorShortcut } from "@/hooks/use-open-editor-shortcut";
import { TrackLocations } from "@/hooks/use-segment";
import { type PlainEditor } from "@/queries/editor-queries";
import { useGetOrganizationPolicies } from "@/queries/organization-queries";
import type { PlainEnvironment } from "@/queries/environment-queries";
import { OpenInEditorKeyBinding } from "@/utils/platform";
import { CheckIcon } from "lucide-react";
import { useRef, type FC, type ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { useFlagIdeMultiVersionEnabled } from "@/hooks/use-flag";

type OpenInEditorButtonProps = {
    environment: PlainEnvironment;
    disabled?: boolean;
};

export const OpenInEditorButton: FC<OpenInEditorButtonProps> = ({ environment, disabled }) => {
    const openEditor = useOpenEditor();
    const {
        selectedEditor,
        selectedEditorVersion,
        isSingleEditor,
        availableEditors,
        isPending: isPendingEditors,
    } = useGetEditorForEnv(environment.id);
    const anchor = useRef<HTMLDivElement>(null);

    // Get the latest version for the selected editor from the server-filtered versions
    // Server returns versions sorted with latest first
    const getLatestAllowedVersion = (editor: PlainEditor): string | undefined => {
        return editor.versions?.[0]?.version;
    };

    useOpenEditorShortcut((triggered) => {
        if (triggered && selectedEditor) {
            void openEditor(
                selectedEditor,
                environment,
                selectedEditorVersion || getLatestAllowedVersion(selectedEditor),
            );
        }
    });

    const tooltip: string | ReactElement = disabled ? (
        "Start environment to open in editor"
    ) : (
        <div className="flex flex-row items-center gap-2">
            Open editor
            <KeyboardCombo keys={OpenInEditorKeyBinding} className="ml-1" />
        </div>
    );

    return (
        <div data-testid="open-in-default-editor" ref={anchor}>
            <SkeletonBlock ready={!!selectedEditor && !isPendingEditors}>
                {selectedEditor && (
                    <ButtonGroup variant="secondary">
                        <Tooltip content={tooltip} usePortal={true}>
                            <ButtonGroupItem
                                onClick={() =>
                                    openEditor(
                                        selectedEditor,
                                        environment,
                                        selectedEditorVersion || getLatestAllowedVersion(selectedEditor),
                                    )
                                }
                                disabled={disabled}
                                LeadingIcon={(_props) => (
                                    <EditorIcon editor={selectedEditor} size="sm" className="shrink-0" />
                                )}
                                data-tracking-id="open-in-selected-editor"
                            >
                                <span>
                                    Open <span className="hidden @xs:inline">{selectedEditor.name}</span>
                                </span>
                            </ButtonGroupItem>
                        </Tooltip>
                        {!isSingleEditor && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <ButtonGroupItem
                                        data-testid="open-editor-selection-popover"
                                        LeadingIcon={IconChevronDownSmall}
                                        aria-label="Expand editor selection"
                                        disabled={disabled}
                                    />
                                </DropdownMenuTrigger>
                                <DropdownMenuPortal>
                                    <DropdownMenuContent
                                        align="end"
                                        data-track-location={TrackLocations.EnvironmentEditorPopover}
                                    >
                                        <EditorSelectionPopover
                                            environment={environment}
                                            editors={availableEditors || []}
                                        />
                                    </DropdownMenuContent>
                                </DropdownMenuPortal>
                            </DropdownMenu>
                        )}
                    </ButtonGroup>
                )}
            </SkeletonBlock>
        </div>
    );
};

type EditorSelectionPopoverProps = {
    environment: PlainEnvironment;
    editors: PlainEditor[];
    onClose?: () => void;
};
const EditorSelectionPopover: FC<EditorSelectionPopoverProps> = ({ environment, editors }) => {
    const navigate = useNavigate();
    const { selectedEditor, selectedEditorVersion } = useGetEditorForEnv(environment.id);
    const { data: organizationPolicies } = useGetOrganizationPolicies();
    const { value: ideMultiVersionEnabled, loading: ideMultiVersionLoading } = useFlagIdeMultiVersionEnabled();

    const showVersionRestrictions = ideMultiVersionEnabled && !ideMultiVersionLoading;

    return (
        <div className="flex flex-col">
            <SkeletonBlock ready={!!editors}>
                <>
                    {editors.map((editor) => (
                        <EditorMenuItem
                            key={editor.id}
                            editor={editor}
                            environment={environment}
                            isSelected={editor.id === selectedEditor?.id}
                            selectedEditorVersion={selectedEditorVersion}
                            showVersionRestrictions={showVersionRestrictions}
                            organizationPolicies={organizationPolicies}
                        />
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                        <DropdownMenuLabel>Other options</DropdownMenuLabel>
                        <DropdownMenuItem
                            onClick={() => navigate(`/settings/preferences`)}
                            data-tracking-id="change-default-editor"
                        >
                            Change default editor
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onClick={() => window.open("https://ona.com/docs/ona/editors/overview", "_blank")}
                            data-tracking-id="troubleshoot-editor"
                        >
                            Troubleshoot
                        </DropdownMenuItem>
                    </DropdownMenuGroup>
                </>
            </SkeletonBlock>
        </div>
    );
};

type EditorMenuItemProps = {
    editor: PlainEditor;
    environment: PlainEnvironment;
    isSelected: boolean;
    selectedEditorVersion?: string;
    showVersionRestrictions: boolean;
    organizationPolicies: ReturnType<typeof useGetOrganizationPolicies>["data"];
};

const EditorMenuItem: FC<EditorMenuItemProps> = ({
    editor,
    environment,
    isSelected,
    selectedEditorVersion,
    showVersionRestrictions,
}) => {
    const openEditor = useOpenEditor();
    const setEditorForEnv = useSetEditorIdForEnv(environment.id);

    // Show version picker if feature flag is on AND editor has multiple versions available
    const shouldShowVersions = showVersionRestrictions && editor.versions.length > 0;

    if (shouldShowVersions) {
        return (
            <EditorWithVersions
                editor={editor}
                environment={environment}
                isSelected={isSelected}
                selectedEditorVersion={selectedEditorVersion}
            />
        );
    }

    // Regular editor without version selection
    return (
        <DropdownMenuItem
            onSelect={() => {
                // Use previously selected version if this is the same editor
                const version = isSelected ? selectedEditorVersion : undefined;
                void openEditor(editor, environment, version);
                void setEditorForEnv(editor.id, version);
            }}
            aria-label={`Open in ${editor.name}`}
            aria-current={isSelected ? "true" : undefined}
            LeadingIcon={(_props) => <EditorIcon editor={editor} size="sm" className="shrink-0" />}
        >
            <span className="min-w-0 flex-1 truncate">{editor.name}</span>
            {isSelected && (
                <div className="flex items-center justify-center gap-2 p-0.5 pl-2">
                    <CheckIcon className="size-4" aria-hidden="true" />
                </div>
            )}
        </DropdownMenuItem>
    );
};

type EditorWithVersionsProps = {
    editor: PlainEditor;
    environment: PlainEnvironment;
    isSelected: boolean;
    selectedEditorVersion?: string;
};
const EditorWithVersions: FC<EditorWithVersionsProps> = ({
    editor,
    environment,
    isSelected,
    selectedEditorVersion,
}) => {
    const openEditor = useOpenEditor();
    const setEditorForEnv = useSetEditorIdForEnv(environment.id);
    const latestVersion = editor.versions[0].version; // First version is the latest

    return (
        <DropdownMenuSub>
            <DropdownMenuSubTrigger>
                <EditorIcon editor={editor} size="sm" className="ml-1 mr-2 shrink-0" />
                <span className="min-w-0 flex-1 truncate">{editor.name}</span>
                {isSelected && (
                    <div className="flex items-center justify-center gap-2 p-0.5 pl-2">
                        <CheckIcon className="size-4 text-content-brand" aria-hidden="true" />
                    </div>
                )}
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
                <DropdownMenuSubContent>
                    {editor.versions.map((version) => {
                        const isLatest = version.version === latestVersion;
                        const isSelectedVersion =
                            selectedEditorVersion === version.version || (!selectedEditorVersion && isLatest);

                        return (
                            <DropdownMenuItem
                                key={version.version}
                                onSelect={() => {
                                    void openEditor(editor, environment, version.version);
                                    void setEditorForEnv(editor.id, version.version);
                                }}
                            >
                                <span className="flex flex-1 items-center gap-2">
                                    <span>{version.version}</span>
                                </span>
                                {isSelectedVersion && isSelected && (
                                    <div className="ml-2 flex items-center justify-center gap-2 p-0.5">
                                        <CheckIcon className="size-4 text-content-brand" aria-hidden="true" />
                                    </div>
                                )}
                            </DropdownMenuItem>
                        );
                    })}
                </DropdownMenuSubContent>
            </DropdownMenuPortal>
        </DropdownMenuSub>
    );
};
